import { landmineNames } from '@mm/constants/landmine-names';
import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import SampleBotCRUDActions from '@mm/state/actions/SampleBotCRUDActions';
import { BotCloneArgs } from '@mm/state/controllers/ChatbotController';
import { BasicChatbot } from '@mm/state/interfaces/chatbot';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectAvailablePages } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import closeImageSrc from 'assets/icons/close.svg';
import rightChevron from 'assets/right-chevron.svg';
import { Formik, FormikProps } from 'formik';
import { History } from 'history';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { usePollTillClonedBot } from '../../../editor/features/templates/components/CreateBotOrExtendByTemplate/CreateBotOrExtendByTemplate';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import Input from '../../components/Input/Input';
import SelectorWithArrow from '../../components/SelectorWithArrow/SelectorWithArrow';
import { PAGES_UPGRADE_COUNT } from '../../constants/payments';
import { RouterComponentProps } from '../../helpers/routes/interfaces';
import { withErrorMessage } from '../../hocs/withErrorMessage/withErrorMessage';
import { getResolutionInfo } from '@mm/hooks';
import { ModalInstanceProps } from '../interfaces';
import { ModalBody, ModalProcessingBlinder } from '../modal-helpers';
import CreateBotImageSection from './CreateBotImageSection';
import CreateBotTypeInput from './CreateBotTypeInput';
import './CreateChatbotModal.scss';
import {
  BOT_CREATE_TYPE,
  CreateBotFormValues,
  getInitialValues,
  getSelectedBot,
  validationSchema,
} from './form-helpers';

type OuterProps = ModalInstanceProps & {
  companyId: number;
  facebookPageId: number;
  bypassFreePlanCheck?: boolean;
  closeModal: Function;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = OuterProps & InnerProps;
type BotToCloneType = ChatbotModel | BasicChatbot | string;
const newChatbotText =
  "This chatbot won't respond on your page until you turn it on";
const BotInput = withErrorMessage(Input);
const TemplateCodeInput = withErrorMessage(Input);

const redirectToBot = (history: History) => (botId: number) => {
  const { isMobile } = getResolutionInfo();

  if (isMobile) {
    history.push(BotBuilderRoutes.dashboard.getLink({ chatbotId: botId }));
  } else {
    history.push(BotBuilderRoutes.build.getLink({ chatbotId: botId }));
  }
};

const CreateBotModal = (props: Props) => {
  const {
    sampleBots,
    bots,
    closeModal,
    errors,
    getSampleBots,
    company,
    retrieveBasicListByCompany,
    createChatbot,
    companyId,
    facebookPageId,
    cloneBot,
    history,
    isStillCloning,
    bypassFreePlanCheck,
  } = props;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const availablePages = useSelector(selectAvailablePages);
  const modalActions = useModalActions();

  useEffect(() => {
    const fetchData = async () => {
      if (company) {
        await Promise.all([
          retrieveBasicListByCompany(company.id),
          getSampleBots(),
        ]);

        setIsFetching(false);
      }
    };
    fetchData();
  }, [company, retrieveBasicListByCompany, getSampleBots]);

  const [pollTillCloned] = usePollTillClonedBot(isStillCloning);

  const needsUpgrade =
    company?.editable &&
    availablePages?.data.length > PAGES_UPGRADE_COUNT &&
    !featureFlags.connectPageUnlimitedPages;

  const memoizedStartCloneChatbot = useCallback(
    async (botToClone: BotToCloneType, botName: string) => {
      setIsProcessing(true);

      const cloneBotBaseParams = {
        facebookPageId,
        name: botName,
        async: true,
      };

      let cloneBotParams: BotCloneArgs;

      // Cloning by template search token
      if (typeof botToClone === 'string') {
        // We don't need to parse ref from url anymore but let's keep this for backward compatibility
        const result = botToClone.match(/ref=?([0-9a-z]+)/);
        const ref = result ? result[1] : botToClone;

        cloneBotParams = {
          ...cloneBotBaseParams,
          ref,
        };
      } else {
        cloneBotParams = {
          ...cloneBotBaseParams,
          botId: botToClone.id,
        };
      }

      try {
        const bot = await cloneBot(cloneBotParams);
        if (bot && bot.id) {
          await pollTillCloned(bot.id);
          redirectToBot(history)(bot.id);

          if (needsUpgrade) {
            modalActions.openUniversalUpgradeLandmineModal({
              landmineName: landmineNames['CREATE-BOT-MODAL'],
              feature: 'connectPageUnlimitedPages',
            });
          } else {
            closeModal();
          }
        }
      } catch (e) {
      } finally {
        setIsProcessing(false);
      }
    },
    [
      createChatbot,
      companyId,
      closeModal,
      setIsProcessing,
      facebookPageId,
      history,
      pollTillCloned,
      needsUpgrade,
    ],
  );

  const memoizedСreateNewChatbot = useCallback(
    async (botName: string) => {
      setIsProcessing(true);

      const bot = await createChatbot({
        name: botName,
        companyId,
        facebookPageId,
      });

      if (bot) {
        redirectToBot(history)(bot.bot.id);
      }

      closeModal();
    },
    [
      createChatbot,
      companyId,
      closeModal,
      setIsProcessing,
      facebookPageId,
      history,
    ],
  );

  const memoizedSubmitForm = useCallback(
    (formValues: CreateBotFormValues) => {
      const requiresCloningFeatureFlags =
        formValues.createType === BOT_CREATE_TYPE.CODE ||
        formValues.createType === BOT_CREATE_TYPE.CLONE;

      if (requiresCloningFeatureFlags && !featureFlags.botBuilderBotCloning) {
        return modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['CREATE-BOT-MODAL'],
          feature: 'botBuilderBotCloning',
        });
      }

      if (!featureFlags.botBuilderUnlimitedBots && !bypassFreePlanCheck) {
        return modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames.CHABOT_LIMIT_REACHED,
          feature: 'botBuilderUnlimitedBots',
        });
      }

      switch (formValues.createType) {
        case BOT_CREATE_TYPE.SCRATCH:
          return memoizedСreateNewChatbot(formValues.botName);
        case BOT_CREATE_TYPE.CLONE:
          return memoizedStartCloneChatbot(
            formValues.selectedCloneBot as BotToCloneType,
            formValues.botName,
          );
        case BOT_CREATE_TYPE.TEMPLATE:
          return memoizedStartCloneChatbot(
            formValues.selectedSampleBot as BotToCloneType,
            formValues.botName,
          );
        case BOT_CREATE_TYPE.CODE:
          return memoizedStartCloneChatbot(
            formValues.templateCode,
            formValues.botName,
          );
      }
    },
    [featureFlags, bypassFreePlanCheck],
  );

  if (isFetching) {
    return null;
  }

  const hasSampleBots = !!sampleBots.length;
  const hasBots = !!bots.length;

  return (
    <div className="create-chatbot-modal">
      <ModalBody className="create-chatbot-modal__body">
        <span
          className="modal-container__header-close-button"
          onClick={closeModal}
        >
          <img src={closeImageSrc} alt="close modal" />
        </span>
        <CreateBotImageSection />
        <div className="create-chatbot-modal__input-section">
          <Formik
            initialValues={getInitialValues({ sampleBots, bots })}
            validationSchema={validationSchema}
            onSubmit={memoizedSubmitForm}
          >
            {(formProps: FormikProps<CreateBotFormValues>) => (
              <form onSubmit={formProps.handleSubmit}>
                <div className="create-chatbot-modal__container">
                  <div className="create-chatbot-modal__form">
                    <div className="create-chatbot-modal__input-group">
                      <div className="create-chatbot-modal__form-label">
                        Chatbot name
                      </div>
                      <BotInput
                        name="botName"
                        type="text"
                        onChange={formProps.handleChange}
                        value={formProps.values.botName}
                        error={formProps.errors.botName}
                        className="create-chatbot-modal__input"
                        placeholder="Enter chatbot name"
                      />
                    </div>
                  </div>
                  <div className="create-chatbot-modal__type-block">
                    <div className="create-chatbot-modal__type-wrapper">
                      <CreateBotTypeInput
                        formProps={formProps}
                        labelText="Build from Scratch"
                        botCreateType={BOT_CREATE_TYPE.SCRATCH}
                        requiresPro={false}
                      />
                    </div>

                    {hasSampleBots && (
                      <div className="create-chatbot-modal__type-wrapper">
                        <CreateBotTypeInput
                          formProps={formProps}
                          labelText="Start from a template"
                          botCreateType={BOT_CREATE_TYPE.TEMPLATE}
                          requiresPro={false}
                        />

                        <div className="create-chatbot-modal__selector-wrapper">
                          <SelectorWithArrow
                            items={sampleBots}
                            selectedItem={formProps.values.selectedSampleBot}
                            onChange={id =>
                              formProps.setFieldValue(
                                'selectedSampleBot',
                                getSelectedBot(id as number, sampleBots),
                              )
                            }
                            disabled={
                              formProps.values.createType !==
                              BOT_CREATE_TYPE.TEMPLATE
                            }
                          />
                        </div>
                      </div>
                    )}
                    <div className="create-chatbot-modal__type-wrapper">
                      <CreateBotTypeInput
                        formProps={formProps}
                        labelText="Paste a template code"
                        botCreateType={BOT_CREATE_TYPE.CODE}
                        requiresPro={true}
                      />
                      <div className="create-chatbot-modal__selector-wrapper">
                        <TemplateCodeInput
                          name="templateCode"
                          type="text"
                          onChange={formProps.handleChange}
                          value={formProps.values.templateCode}
                          error={
                            formProps.errors.templateCode || errors.templateCode
                          }
                          className="create-chatbot-modal__input"
                          placeholder="Enter code"
                          disabled={
                            formProps.values.createType !== BOT_CREATE_TYPE.CODE
                          }
                        />
                      </div>
                    </div>

                    {hasBots && (
                      <div className="create-chatbot-modal__type-wrapper">
                        <CreateBotTypeInput
                          formProps={formProps}
                          labelText="Copy your existing chatbot"
                          botCreateType={BOT_CREATE_TYPE.CLONE}
                          requiresPro={true}
                        />

                        <div className="create-chatbot-modal__selector-wrapper">
                          <SelectorWithArrow
                            items={bots}
                            selectedItem={formProps.values.selectedCloneBot}
                            onChange={id =>
                              formProps.setFieldValue(
                                'selectedCloneBot',
                                getSelectedBot(id as number, bots),
                              )
                            }
                            disabled={
                              formProps.values.createType !==
                              BOT_CREATE_TYPE.CLONE
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="create-chatbot-modal__footer">
                  <p className="create-chatbot-modal__info">{newChatbotText}</p>
                  <div className="create-chatbot-modal__actions">
                    <Button
                      type="submit"
                      className="create-chatbot-modal__action"
                      disabled={!formProps.isValid}
                    >
                      Create bot
                      <img
                        src={rightChevron}
                        className="create-chatbot-modal__action--image"
                        alt="Create bot icon"
                      />
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </ModalBody>

      <ModalProcessingBlinder show={isProcessing} />
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  createChatbot: entity => dispatch(chatbotCRUDActions.create(entity)),
  cloneBot: (botCloneArgs: BotCloneArgs) =>
    dispatch(chatbotCRUDActions.cloneBot(botCloneArgs)),
  isStillCloning: async (botId: number) => {
    const maybeBot = await dispatch(chatbotCRUDActions.retrieve(botId, true));
    return maybeBot ? maybeBot.cloningProcess : true;
  },
  getSampleBots: () => dispatch(SampleBotCRUDActions.retrieveAll()),
  retrieveBasicListByCompany: (companyId: number) =>
    dispatch(chatbotCRUDActions.retrieveBasicListByCompany(companyId)),
});

const mapStateToProps = (state: StoreState) => ({
  sampleBots: state.sampleBots.entities,
  bots: state.chatbot.basicListByCompany.filter(bot => !bot.template),
  errors: state.chatbot.errors,
  sessionId: state.session.session.id,
  company: activeCompanySelector(state),
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(CreateBotModal);
